import { type RefObject,useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uPlot from 'uplot';

import type { ChartSeries, TooltipInfo } from './types';
import { buildSeriesStructureKey } from './uplot-data-adapter';

type UseUPlotOptions = {
  targetRef: RefObject<HTMLDivElement | null>;
  options: uPlot.Options;
  data: uPlot.AlignedData;
  series: ChartSeries[];
  hiddenSeriesIds?: Set<string>;
};

type UseUPlotReturn = {
  tooltipInfo: TooltipInfo | null;
  activeSeriesId: string | null;
  zoomControls: {
    canReset: boolean;
    reset: () => void;
  };
  interactionHandlers: {
    onMouseMove: () => void;
    onMouseLeave: () => void;
  };
  tooltipHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  tooltipActions: {
    lock: () => void;
    unlock: () => void;
  };
};

const TOOLTIP_DISMISS_DELAY_MS = 150;

export const useUPlot = ({
  targetRef,
  options,
  data,
  series,
  hiddenSeriesIds,
}: UseUPlotOptions): UseUPlotReturn => {
  const [tooltipInfo, setTooltipInfo] = useState<TooltipInfo | null>(null);
  const [activeSeriesId, setActiveSeriesId] = useState<string | null>(null);
  const [canResetZoom, setCanResetZoom] = useState(false);

  const chartRef = useRef<uPlot | null>(null);
  const initialXScaleRef = useRef<{ min: number; max: number } | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTooltipHoveredRef = useRef(false);
  const isTooltipLockedRef = useRef(false);
  const activeSeriesIdRef = useRef<string | null>(null);

  const dataRef = useRef(data);
  const seriesRef = useRef(series);
  const hiddenSeriesIdsRef = useRef(hiddenSeriesIds);

  dataRef.current = data;
  seriesRef.current = series;
  hiddenSeriesIdsRef.current = hiddenSeriesIds;

  const seriesStructureKey = useMemo(() => buildSeriesStructureKey(series), [series]);

  const syncResetState = useCallback((chart: uPlot) => {
    const min = chart.scales.x.min;
    const max = chart.scales.x.max;

    if (min == null || max == null) {
      return;
    }

    if (!initialXScaleRef.current) {
      initialXScaleRef.current = { min, max };
      setCanResetZoom(false);
      return;
    }

    const epsilon = 1e-7;
    const base = initialXScaleRef.current;
    const changed = Math.abs(base.min - min) > epsilon || Math.abs(base.max - max) > epsilon;
    setCanResetZoom(changed);
  }, []);

  const clearDismissTimer = useCallback(() => {
    if (!hideTimerRef.current) {
      return;
    }

    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);

  const dismissTooltip = useCallback(() => {
    setTooltipInfo(null);
    activeSeriesIdRef.current = null;
    setActiveSeriesId(null);
  }, []);

  const scheduleDismiss = useCallback(() => {
    clearDismissTimer();
    hideTimerRef.current = setTimeout(() => {
      if (isTooltipHoveredRef.current || isTooltipLockedRef.current) {
        return;
      }

      dismissTooltip();
    }, TOOLTIP_DISMISS_DELAY_MS);
  }, [clearDismissTimer, dismissTooltip]);

  const syncTooltipFromCursor = useCallback(
    (chart: uPlot) => {
      if (isTooltipHoveredRef.current || isTooltipLockedRef.current) {
        return;
      }

      const cursorIndex = chart.cursor.idx;
      if (cursorIndex == null || cursorIndex < 0) {
        return;
      }

      const xColumn = chart.data[0] as Array<number | null | undefined>;
      const xSeconds = xColumn[cursorIndex];
      if (xSeconds == null) {
        return;
      }

      const visibleSeries = seriesRef.current
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !hiddenSeriesIdsRef.current?.has(item.id));
      if (visibleSeries.length === 0) {
        dismissTooltip();
        return;
      }

      const points = visibleSeries.map(({ item, index }) => {
        const yColumn = chart.data[index + 1] as Array<number | null | undefined>;
        const yValue = yColumn?.[cursorIndex];

        return {
          seriesId: item.id,
          label: item.label,
          color: item.color,
          value: typeof yValue === 'number' ? yValue : 0,
          rawValue: yValue,
        };
      });

      let nearestSeriesId: string | null = null;
      let nearestSeriesValue: number | null = null;
      let minDistance = Number.POSITIVE_INFINITY;

      for (let index = 0; index < points.length; index += 1) {
        const point = points[index];
        if (typeof point.rawValue !== 'number') {
          continue;
        }

        const yPos = chart.valToPos(point.rawValue, 'y', false);
        const distance = Math.abs((chart.cursor.top ?? 0) - yPos);

        if (distance < minDistance) {
          minDistance = distance;
          nearestSeriesId = point.seriesId;
          nearestSeriesValue = point.rawValue;
        }
      }

      // chart.over = uPlot 내부 플롯 영역 (축 제외)
      // targetRef 기준의 오프셋을 계산해야 툴팁이 올바른 위치에 렌더링됨
      const targetEl = targetRef.current;
      const overEl = chart.over;
      let plotOffsetX = 0;
      let plotOffsetY = 0;
      if (targetEl && overEl) {
        const overRect = overEl.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        plotOffsetX = overRect.left - targetRect.left;
        plotOffsetY = overRect.top - targetRect.top;
      }

      // CSS 픽셀 기준 + 플롯 영역 오프셋 적용
      const cursorLeft = (chart.cursor.left ?? 0) + plotOffsetX;
      const cursorTop = (chart.cursor.top ?? 0) + plotOffsetY;

      // valToPos(val, axis, false) → CSS 픽셀, 플롯 영역 기준
      const anchorX = chart.valToPos(xSeconds, 'x', false) + plotOffsetX;
      const anchorY =
        nearestSeriesValue == null
          ? cursorTop
          : chart.valToPos(nearestSeriesValue, 'y', false) + plotOffsetY;
      setTooltipInfo({
        x: cursorLeft,
        y: cursorTop,
        anchorX,
        anchorY,
        xIndex: cursorIndex,
        xValue: xSeconds * 1000,
        points: points.map((point) => ({
          seriesId: point.seriesId,
          label: point.label,
          color: point.color,
          value: point.value,
          rawValue: typeof point.rawValue === 'number' ? point.rawValue : null,
        })),
        activeSeriesId: nearestSeriesId,
      });

      if (nearestSeriesId !== activeSeriesIdRef.current) {
        activeSeriesIdRef.current = nearestSeriesId;
        setActiveSeriesId(nearestSeriesId);
      }
    },
    [dismissTooltip],
  );

  const handleMouseMove = useCallback(() => {
    clearDismissTimer();
  }, [clearDismissTimer]);

  const handleMouseLeave = useCallback(() => {
    scheduleDismiss();
  }, [scheduleDismiss]);

  const handleTooltipMouseEnter = useCallback(() => {
    isTooltipHoveredRef.current = true;
    clearDismissTimer();
  }, [clearDismissTimer]);

  const handleTooltipMouseLeave = useCallback(() => {
    isTooltipHoveredRef.current = false;
    scheduleDismiss();
  }, [scheduleDismiss]);

  const handleTooltipLock = useCallback(() => {
    isTooltipLockedRef.current = true;
    clearDismissTimer();
  }, [clearDismissTimer]);

  const handleTooltipUnlock = useCallback(() => {
    isTooltipLockedRef.current = false;
    scheduleDismiss();
  }, [scheduleDismiss]);

  const handleResetZoom = useCallback(() => {
    const chart = chartRef.current;
    const initialXScale = initialXScaleRef.current;

    if (!chart || !initialXScale) {
      return;
    }

    chart.setScale('x', {
      min: initialXScale.min,
      max: initialXScale.max,
    });
  }, []);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) {
      return;
    }

    const height = Math.max(target.clientHeight, 1);
    const width = Math.max(target.clientWidth, 1);

    const chartOptions: uPlot.Options = {
      ...options,
      width,
      height,
      hooks: {
        ...options.hooks,
        ready: [...(options.hooks?.ready ?? []), (chart) => syncResetState(chart)],
        setScale: [...(options.hooks?.setScale ?? []), (chart) => syncResetState(chart)],
        setCursor: [...(options.hooks?.setCursor ?? []), (chart) => syncTooltipFromCursor(chart)],
      },
    };

    const nextChart = new uPlot(chartOptions, dataRef.current, target);
    chartRef.current = nextChart;

    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(() => {
      const nextWidth = Math.max(target.clientWidth, 1);
      const nextHeight = Math.max(target.clientHeight, 1);
      nextChart.setSize({ width: nextWidth, height: nextHeight });
    });
    resizeObserverRef.current.observe(target);

    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      nextChart.destroy();
      if (chartRef.current === nextChart) {
        chartRef.current = null;
      }
    };
  }, [options, seriesStructureKey, syncResetState, syncTooltipFromCursor, targetRef]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    chart.setData(data);
  }, [data]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    for (let index = 0; index < series.length; index += 1) {
      const item = series[index];
      chart.setSeries(index + 1, { show: !hiddenSeriesIds?.has(item.id) });
    }
  }, [hiddenSeriesIds, series]);

  useEffect(() => {
    return () => {
      clearDismissTimer();
      dismissTooltip();
      initialXScaleRef.current = null;
      setCanResetZoom(false);
    };
  }, [clearDismissTimer, dismissTooltip]);

  return {
    tooltipInfo,
    activeSeriesId,
    zoomControls: {
      canReset: canResetZoom,
      reset: handleResetZoom,
    },
    interactionHandlers: {
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave,
    },
    tooltipHandlers: {
      onMouseEnter: handleTooltipMouseEnter,
      onMouseLeave: handleTooltipMouseLeave,
    },
    tooltipActions: {
      lock: handleTooltipLock,
      unlock: handleTooltipUnlock,
    },
  };
};
